import React, { useEffect, useRef, useState } from 'react';
import { FiMail, FiTrendingUp, FiUsers, FiArrowRight, FiZap, FiTarget, FiShield, FiStar } from 'react-icons/fi';

const EnginesSection = () => {
  const [isVisible, setIsVisible] = useState({
    header: false,
    cards: [],
    bottom: false
  });

  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);
  const bottomRef = useRef(null);

  const engines = [
    {
      number: '01',
      icon: <FiZap className="w-6 h-6" />,
      title: 'Scalable Architecture',
      subtitle: 'ENGINE 01',
      description: "We design and build robust, future-ready architectures that scale with your business. No patchwork systems or fragile foundations—just clean, efficient, and extensible codebases built for long-term growth.",
      points: [
        'Modern, Scalable Tech Stack',
        'Clean & Maintainable Code Architecture',
        'Built for Performance & Growth'
      ],
      gradient: 'from-cyan-500 to-emerald-500',
      iconBg: 'from-cyan-500/20 to-emerald-500/20',
      stat: '99.9%',
      statLabel: 'System Reliability',
      iconColor: 'text-cyan-400'
    },
    {
      number: '02',
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Agile Development Process',
      subtitle: 'ENGINE 02',
      description: "Fast, transparent, and iterative development cycles that keep you in control. We ship features quickly, gather feedback, and continuously improve—so your product evolves with real user needs.",
      points: [
        'Sprint-Based Development',
        'Rapid Feature Delivery',
        'Continuous Testing & Improvements'
      ],
      gradient: 'from-violet-500 to-purple-500',
      iconBg: 'from-violet-500/20 to-purple-500/20',
      stat: '2x',
      statLabel: 'Faster Delivery',
      iconColor: 'text-violet-400'
    },
    {
      number: '03',
      icon: <FiShield className="w-6 h-6" />,
      title: 'Performance & Security',
      subtitle: 'ENGINE 03',
      description: "We ensure your software is fast, secure, and reliable under real-world conditions. From optimization to security best practices, we build systems that users trust and businesses depend on.",
      points: [
        'High-Speed Performance Optimization',
        'Advanced Security Standards',
        'Reliable & Stable Systems'
      ],
      gradient: 'from-amber-500 to-orange-500',
      iconBg: 'from-amber-500/20 to-orange-500/20',
      stat: '100%',
      statLabel: 'Secure Code Standards',
      iconColor: 'text-amber-400'
    }
  ];

  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    };

    const headerObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, header: true }));
          headerObserver.disconnect();
        }
      });
    }, observerOptions);

    const cardObservers = cardsRef.current.map((card, index) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              cards: [...prev.cards, index]
            }));
            observer.disconnect();
          }
        });
      }, { ...observerOptions, threshold: 0.1 });

      if (card) observer.observe(card);
      return observer;
    });

    const bottomObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, bottom: true }));
          bottomObserver.disconnect();
        }
      });
    }, observerOptions);

    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (bottomRef.current) bottomObserver.observe(bottomRef.current);

    return () => {
      headerObserver.disconnect();
      cardObservers.forEach(observer => observer.disconnect());
      bottomObserver.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-black text-white py-28 px-6 font-sans relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/5 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-[120px] animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[150px]"></div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(14,214,183,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(14,214,183,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header Section with Animation */}
        <div
          ref={headerRef}
          className={`text-center transition-all duration-700 delay-200 transform ${isVisible.header ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}
        >
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 backdrop-blur-sm px-4 py-2 rounded-full border border-emerald-500/30 mb-6">
            <FiZap className="text-emerald-400 w-4 h-4 animate-pulse" />
            <span className="text-emerald-400 text-xs font-bold tracking-[2px] uppercase">FROM IDEA TO DEPLOYMENT</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            Three Pillars. One
            <span className="relative inline-block mx-2">
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Scalable Software.
              </span>
              <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 200 8" fill="none">
                <path d="M0 4 L200 4" stroke="url(#gradientLine)" strokeWidth="2" strokeDasharray="5 5" />
                <defs>
                  <linearGradient id="gradientLine" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>

          <p className="max-w-2xl mx-auto text-gray-400 text-lg mb-20 leading-relaxed">
            We don't spray and pray. We run three high-signal channels in concert - each designed to reach, warm, and qualify your ideal buyers before they ever hit your calendar.
          </p>
        </div>

        {/* Cards Grid with Staggered Animations */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-24">
          {engines.map((engine, index) => (
            <div
              key={index}
              ref={el => cardsRef.current[index] = el}
              className={`group transition-all duration-700 transform ${isVisible.cards.includes(index)
                ? 'translate-y-0 opacity-100'
                : 'translate-y-16 opacity-0'
                }`}
              style={{ transitionDelay: `${200 + index * 150}ms` }}
            >
              <div className="relative h-full bg-gradient-to-br from-gray-900/90 to-gray-950/90 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-emerald-500/10 hover:-translate-y-2">

                {/* Animated Gradient Border on Hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className={`absolute inset-0 bg-gradient-to-r ${engine.gradient} opacity-20 blur-xl`}></div>
                </div>

                {/* Background Number */}
                <div className="absolute top-8 right-8 text-8xl font-black text-gray-800/30 select-none group-hover:text-gray-700/40 transition-all duration-500 group-hover:scale-110">
                  {engine.number}
                </div>

                <div className="p-8 relative z-10">
                  {/* Icon Container with Animation - FIXED */}
                  <div className="flex justify-end mb-8">
                    <div className={`bg-gradient-to-br ${engine.iconBg} p-3 rounded-xl border border-gray-700 group-hover:border-transparent transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 relative overflow-hidden`}>
                      <div className={`absolute inset-0 bg-gradient-to-r ${engine.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
                      {/* Icon rendered directly without gradient text wrapper */}
                      <div className={engine.iconColor}>
                        {engine.icon}
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <span className={`text-transparent bg-gradient-to-r ${engine.gradient} bg-clip-text text-[10px] font-bold tracking-widest block mb-2`}>
                      {engine.subtitle}
                    </span>
                    <h3 className="text-2xl font-bold mb-4 tracking-tight group-hover:text-white transition-colors">
                      {engine.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-6 group-hover:text-gray-300 transition-colors">
                      {engine.description}
                    </p>

                    {/* Stats Badge */}
                    <div className="mb-6 p-3 bg-gray-800/30 rounded-lg border border-gray-700/50 group-hover:border-gray-600 transition-all">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Performance</span>
                        <div className="flex items-center gap-1">
                          <FiStar className={`${engine.iconColor} w-3 h-3`} />
                          <span className={`text-lg font-bold bg-gradient-to-r ${engine.gradient} bg-clip-text text-transparent`}>
                            {engine.stat}
                          </span>
                          <span className="text-xs text-gray-500">{engine.statLabel}</span>
                        </div>
                      </div>
                    </div>

                    {/* List Points with Icons */}
                    <ul className="space-y-3">
                      {engine.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                          <div className={`mt-1 w-1.5 h-1.5 min-w-[6px] rounded-full bg-gradient-to-r ${engine.gradient}`} />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Animated Bottom Bar */}
                <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${engine.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Bottom Detailed Summary with Animation */}
        <div
          ref={bottomRef}
          className={`max-w-5xl mx-auto transition-all duration-700 delay-300 transform ${isVisible.bottom ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}
        >
          <div className="relative group">
            {/* Animated Border Glow */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 via-cyan-500 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-50 blur transition duration-500"></div>

            <div className="relative bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 md:p-10 group-hover:border-emerald-500/30 transition-all duration-500">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/5 rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500/5 rounded-full blur-2xl"></div>

              <div className="relative z-10">
                {/* Quote Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center border border-emerald-500/30">
                    <svg className="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                </div>

                <p className="text-gray-300 text-lg md:text-xl leading-relaxed text-center">
                  The result: when your product goes live, it's built on a
                  <span className="relative inline-block mx-1 group/highlight">
                    <span className="text-emerald-400 font-semibold">scalable architecture</span>
                    <svg className="absolute -bottom-1 left-0 w-full h-2" viewBox="0 0 200 8" fill="none">
                      <path d="M0 6 L200 6" stroke="url(#waveGrad)" strokeWidth="1.5" strokeDasharray="4 4" />
                    </svg>
                  </span>,
                  powered by
                  <span className="text-emerald-400 font-semibold mx-1">fast, iterative development</span>,
                  and supported by
                  <span className="text-emerald-400 font-semibold mx-1">secure, high-performance systems</span>.
                  This isn't just development. It's software engineered to scale, perform, and grow with your business.
                </p>

                <defs>
                  <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>

                {/* CTA Button */}
                <div className="flex justify-center mt-8">
                  <button className="group/btn relative inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full font-semibold text-black hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-105">
                    <span>Start Your Software Project</span>
                    <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Trust Badge */}
        <div className="flex justify-center mt-12 opacity-60 hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-6 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <FiShield className="w-3 h-3" />
              <span>No Long-term Contracts</span>
            </div>
            <div className="flex items-center gap-2">
              <FiTarget className="w-3 h-3" />
              <span>Performance Guaranteed</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnginesSection;